import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Sparkles, Heart, Apple, ShieldAlert, Baby, ChevronRight } from 'lucide-react';
import { api } from './api';
import { MaternalBackLink, MaternalDisclaimer, MaternalLoader, MaternalEmptyState } from './_ui';

/**
 * Maternal · Weekly Guidance
 *
 * Pulls /api/maternal/week and renders a calm, single-screen summary of the
 * current pregnancy week (baby + mother + focus + nutrition + red flags) or
 * the current postpartum week. No alarming wording — red flags are framed
 * as "When to call your clinician" rather than panic copy.
 */

const SECTIONS = [
  { key: 'baby',      label: 'Baby this week',     icon: Baby,   accent: 'bg-pink-50 text-pink-700' },
  { key: 'mother',    label: 'How you may feel',   icon: Heart,  accent: 'bg-rose-50 text-rose-700' },
  { key: 'focus',     label: 'Gentle focus',       icon: Sparkles, accent: 'bg-amber-50 text-amber-700' },
  { key: 'nutrition', label: 'Nutrition this week', icon: Apple,  accent: 'bg-emerald-50 text-emerald-700' },
];

const MaternalWeek = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .weekInsight()
      .then((res) => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <MaternalLoader label="Reading this week's guidance…" testid="maternal-week-loading" />;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-week-empty">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <MaternalBackLink />
          <MaternalEmptyState
            icon={BookOpen}
            title="Set your stage to see weekly guidance"
            body="Once you tell us your last menstrual period or postpartum start date, we'll surface gentle, week-by-week support here."
            ctaLabel="Set stage"
            onCta={() => navigate('/maternal-health')}
          />
        </div>
      </div>
    );
  }

  const isPostpartum = data.stage === 'postpartum';
  const title = data.title;
  const redFlags = data.red_flags || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-week">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
              <BookOpen className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[11px] font-semibold text-rose-600 uppercase tracking-widest">This week's guidance</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight" data-testid="maternal-week-title">
                {title}
              </h1>
            </div>
          </div>
        </header>

        {/* Pregnancy: 4 calm sections */}
        {!isPostpartum && (
          <div className="space-y-3">
            {SECTIONS.map((s) => {
              const value = data[s.key];
              if (!value) return null;
              const Ic = s.icon;
              return (
                <article
                  key={s.key}
                  className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5"
                  data-testid={`maternal-week-${s.key}`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.accent}`}>
                      <Ic className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">
                        {s.label}
                      </p>
                      <p className="text-sm text-gray-800 leading-relaxed">{value}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Postpartum view */}
        {isPostpartum && (
          <article className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-3">
            <div className="space-y-3">
              {data.body && (
                <p className="text-sm text-gray-800 leading-relaxed">{data.body}</p>
              )}
              {data.focus && (
                <div>
                  <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest mb-1">Gentle focus</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{data.focus}</p>
                </div>
              )}
              {data.warning && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-[11px] font-bold text-amber-900 uppercase tracking-widest mb-1">When to reach out</p>
                  <p className="text-sm text-amber-900 leading-relaxed">{data.warning}</p>
                </div>
              )}
            </div>
          </article>
        )}

        {/* Red-flag panel — calm, framed as "when to call" not "danger" */}
        {!isPostpartum && redFlags.length > 0 && (
          <article
            className="mt-3 rounded-2xl bg-rose-50 border-2 border-rose-200 p-5"
            data-testid="maternal-week-redflags"
          >
            <div className="flex items-start gap-3 mb-2">
              <ShieldAlert className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-rose-900 leading-tight">When to contact your clinician</p>
                <p className="text-xs text-rose-800 leading-relaxed mt-0.5">
                  These signs aren't routine. If you notice any of them, please call your midwife, GP, or maternity assessment unit today.
                </p>
              </div>
            </div>
            <ul className="space-y-1.5 mt-3">
              {redFlags.map((f) => (
                <li key={f} className="text-sm text-rose-900 flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-2" aria-hidden="true" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </article>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/maternal-health/checkin')}
            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
            data-testid="maternal-week-checkin-cta"
          >
            Log today's check-in
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => navigate('/maternal-health/nutrition')}
            className="inline-flex items-center gap-1.5 bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-800 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
            data-testid="maternal-week-nutrition-cta"
          >
            See nutrition focus
          </button>
        </div>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalWeek;
